import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const isSupabaseConfigured = Boolean(import.meta.env.VITE_SUPABASE_URL) && Boolean(import.meta.env.VITE_SUPABASE_ANON_KEY);

interface AuthContextType {
  user: any;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const SupabaseAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (!isSupabaseConfigured) {
      console.error('Supabase is not configured. Please check your environment variables.');
      setLoading(false);
      return;
    }

    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state (signed in, signed out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Error signing in",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      
      // If user was created successfully, initialize their subscription
      if (data.user) {
        try {
          const { subscriptionService } = await import('../services/subscriptionService');
          await subscriptionService.initializeUserSubscription(data.user.id);
          console.log('User subscription initialized successfully');
        } catch (subscriptionError) {
          console.error('Error initializing user subscription:', subscriptionError);
          // Don't throw here, as the user account was created successfully
        }
      }
    } catch (error: any) {
      toast({
        title: "Error signing up",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clear any local storage items first
      localStorage.removeItem('myhu-auth-token');
      localStorage.removeItem('sb-mrrvqgadxydgllhnkbsv-auth-token');
      sessionStorage.removeItem('redirectAfterAuth');
      sessionStorage.removeItem('triggerOnboarding');
      sessionStorage.removeItem('onboardingEmail');
      
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
      
      // Force a page refresh to clear any cached auth state
      window.location.href = '/';
    } catch (error: any) {
      // If there's an auth session missing error, just clear local state and redirect
      if (error.message?.includes('Auth session missing')) {
        console.log('Session already cleared, redirecting...');
        // Clear all possible auth-related items
        Object.keys(localStorage).forEach(key => {
          if (key.includes('auth') || key.includes('supabase') || key.includes('sb-')) {
            localStorage.removeItem(key);
          }
        });
        sessionStorage.clear();
        window.location.href = '/';
        return;
      }
      
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};

// Alias for backward compatibility
export const useSupabaseAuth = useAuth;

// AuthModal context for global modal state
interface AuthModalContextType {
  showLogin: boolean;
  setShowLogin: (show: boolean) => void;
}
const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export const AuthModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showLogin, setShowLogin] = useState(false);
  return (
    <AuthModalContext.Provider value={{ showLogin, setShowLogin }}>
      {children}
    </AuthModalContext.Provider>
  );
};

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (context === undefined) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
};
